import { isEqual } from 'lodash';
import PropTypes from 'prop-types';
import { useMemo, useState, useRef, useEffect } from 'react';

import { Box } from '@hivebrite/design-system/Box';
import { FontAwesomeIcon } from '@hivebrite/design-system/FontAwesomeIcon';
import { ScreenReadersContent } from '@hivebrite/design-system/ScreenReadersContent';
import { useIsMounted } from '@hivebrite/react-hooks';

import { useDefaultAppContexts } from 'hb-react/frontoffice/contexts/withDefaultAppContexts';
import ReactionsIcon from 'hb-react/shared/components/DesignSystem/Reactions/components/ReactionsIcon';
import ReactionsWrapper from 'hb-react/shared/components/DesignSystem/Reactions/components/ReactionsWrapper';
import onEnterOrSpacePress from 'hb-react/shared/utils/onEnterOrSpacePress';

const FAKE_ID = -1;

const ReactionsLikes = ({
  initialLikes,
  onTogglePostLike,
  type,
  setLikes: setExternalLikes,
  showFullText = true,
  onLikeCallback,
  height = '20px',
}) => {
  const { currentUser } = useDefaultAppContexts();

  const [likesState, setLikesState] = useState(initialLikes);
  const [previousInitialLikes, setPreviousInitialLikes] =
    useState(initialLikes);

  const updateLikes = setExternalLikes ?? setLikesState;
  const likes = setExternalLikes ? initialLikes : likesState;

  const [inProgress, setInProgress] = useState(false);

  const isFirstRun = useRef(true);

  const isMounted = useIsMounted();

  const currentUserHasLiked = useMemo(() => likes.id !== null, [likes]);

  const [hasJustLikedOnce, setHasJustLikedOnce] = useState(false);

  // initialLikes is non-memoized object (e.g. app/assets/javascripts/react/frontoffice/apps/MediaCenter/components/FilesRenderer/index.jsx)
  // and as such changes every render which in turn made the effect below trigger every render and wrongly "reset" the likes state in some scenarios.
  // Using this approach, the useEffect will only trigger when initialLikes property values change as is intended.
  const initialLikesChanged = !isEqual(previousInitialLikes, initialLikes);

  useEffect(() => {
    setPreviousInitialLikes(initialLikes);
    if (!isEqual(likes, initialLikes)) {
      updateLikes(initialLikes);
    }
  }, [initialLikesChanged]);

  useEffect(() => {
    if (isFirstRun.current) {
      isFirstRun.current = false;

      return;
    }
    if (onLikeCallback && likes.id !== FAKE_ID) {
      onLikeCallback(likes);
    }
  }, [likes]);

  const handleToggleLike = async (event) => {
    if (inProgress) {
      return;
    }

    setInProgress(true);
    const likesSaved = { ...likes };

    try {
      if (!likesSaved.id) {
        updateLikes((prevLikes) => ({
          ...prevLikes,
          id: FAKE_ID, // fake id for optimistic update
          count: prevLikes.count + 1,
        }));
        setHasJustLikedOnce(true);
      } else {
        updateLikes((prevLikes) => ({
          ...prevLikes,
          id: null,
          count: prevLikes.count - 1,
        }));
      }

      const like = await onTogglePostLike({
        likeId: likesSaved.id,
        likeableId: likesSaved.likeable_id || likesSaved.likeableId,
        type,
        userId: currentUser.id,
      });

      if (like && isMounted()) {
        updateLikes((prevLikes) => ({
          ...prevLikes,
          id: like.id,
        }));
      }
    } catch {
      updateLikes(likesSaved);
    } finally {
      setInProgress(false);
    }
    event.preventDefault();
  };

  return (
    <ReactionsWrapper
      role="button"
      tabIndex={0}
      onClick={handleToggleLike}
      onKeyPress={onEnterOrSpacePress(handleToggleLike)}
      data-testid={currentUserHasLiked ? 'unlike-action' : 'like-action'}
      aria-label={currentUserHasLiked ? __('Remove like') : __('Like')}
    >
      <ReactionsIcon color={currentUserHasLiked ? 'primary' : undefined}>
        <FontAwesomeIcon
          height={height}
          iconName="heart"
          iconType={currentUserHasLiked ? 'solid' : 'light'}
          {...(currentUserHasLiked &&
            hasJustLikedOnce && {
              animation: 'beat',
              animationDuration: '0.5s',
              animationIterationCount: '1',
            })}
        />
      </ReactionsIcon>
      {showFullText
        ? __('Like')
        : likesState.count > 0 && (
            <Box display="block" mr={2}>
              {likesState.count}
            </Box>
          )}
      {inProgress && (
        <ScreenReadersContent aria-live="polite">
          {s__(
            currentUserHasLiked
              ? 'ReactionsAccessibility|Like selected - %{totalLikes} on this content'
              : 'ReactionsAccessibility|Like unselected - %{totalLikes} on this content',
          ).i__({
            totalLikes: likes.count,
          })}
        </ScreenReadersContent>
      )}
    </ReactionsWrapper>
  );
};

export default ReactionsLikes;

ReactionsLikes.propTypes = {
  height: PropTypes.string,
  onTogglePostLike: PropTypes.func.isRequired,
  type: PropTypes.string.isRequired,
  initialLikes: PropTypes.shape({
    count: PropTypes.number,
    id: PropTypes.number,
    likeable_id: PropTypes.number, // when using axiosInstance
    likeableId: PropTypes.number, // when using axiosWithSyntaxHandlers
    users: PropTypes.array,
  }).isRequired,
  showFullText: PropTypes.bool,
  onLikeCallback: PropTypes.func,
  setLikes: PropTypes.func,
};
